import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Undo2, ExternalLink, Loader2, FileX, FileText } from 'lucide-react';
import { cn } from '../lib/utils';
import {
    createReceiptExpense,
    updateReceiptExpense,
    removeReceiptAttachment,
    deleteExpenseV2,
    uploadReceiptFile,
    getFxPreview,
    getReceiptFileBlob,
    createBulkExpense,
} from '../lib/api';
import { hapticSave } from '../lib/haptics';
import { formatSEK } from '../lib/format-utils';
import { compressReceiptImage } from '../lib/imageCompression';
import SupplierInvoicePreview from './supplier-invoice-preview';

// ── Constants mirrored from backend/config/expense_config.py ──────────────────
const RECEIPT_TYPES = [
    { id: 'travel_flight', name: 'Travel - Flights' },
    { id: 'travel_train', name: 'Travel - Train/Bus' },
    { id: 'travel_taxi', name: 'Travel - Taxi/Uber' },
    { id: 'travel_rental', name: 'Travel - Rental Car' },
    { id: 'travel_fuel', name: 'Travel - Fuel' },
    { id: 'travel_parking', name: 'Travel - Parking' },
    { id: 'travel_tolls', name: 'Travel - Tolls' },
    { id: 'accommodation', name: 'Accommodation' },
    { id: 'meals_client', name: 'Meals - Client' },
    { id: 'meals_business', name: 'Meals - Business' },
    { id: 'office_supplies', name: 'Office Supplies' },
    { id: 'software', name: 'Software/Subscriptions' },
    { id: 'equipment', name: 'Equipment' },
    { id: 'conference', name: 'Conference/Training' },
    { id: 'phone_internet', name: 'Phone/Internet' },
    { id: 'other', name: 'Other' },
];

const PAYMENT_METHODS = [
    { id: 'personal_card', name: 'Personal Card' },
    { id: 'company_card', name: 'Company Card' },
    { id: 'cash', name: 'Cash' },
];

const CURRENCIES = ['SEK', 'EUR', 'USD', 'GBP'];

// ── Accepted MIME types — must match backend _ALLOWED_MIME_TYPES ───────────────
const FILE_ACCEPT = 'application/pdf,image/jpeg,image/png,image/heic,image/heif';

// ── Image extensions we can render inline as a thumbnail ───────────────────────
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'heic', 'heif']);

/**
 * Renders a legacy base64 data-URL image for receipts that still carry
 * receipt_data (not yet migrated to disk). PDFs return null — caller's View
 * button handles them.
 */
function LegacyDataUrlThumbnail({ receiptData, receiptFilename, onClick }) {
    if (!receiptData) return null;
    // Check if it's an image — either by data-URL prefix or filename extension
    const isImage = receiptData.startsWith('data:image') ||
        (receiptFilename && IMAGE_EXTENSIONS.has(receiptFilename.split('.').pop().toLowerCase()));
    if (!isImage) return null;
    // If the data isn't already a full data URL, leave it — fallback to View button
    if (!receiptData.startsWith('data:')) return null;
    return (
        <img
            src={receiptData}
            alt="Receipt"
            className="max-h-40 rounded border object-contain cursor-pointer hover:opacity-80"
            onClick={onClick}
            title="Click to open full size"
        />
    );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function seedRowFromExpense(exp) {
    return {
        id: exp.id,
        receipt_type: exp.receipt_type || '',
        amount: exp.amount != null ? String(exp.amount) : '',
        currency: exp.currency || 'SEK',
        description: exp.description || '',
        payment_method: exp.payment_method || 'personal_card',
        project_id: exp.project_id || '',
        // Phase 23 (PRD 12.1): activity_id is the join key, activity_name the
        // snapshot resolved server-side — never re-derived from it on read.
        activity_id: exp.activity_id || '',
        activity_name: exp.activity_name || '',
        billable: exp.billable ?? false,
        notes: exp.notes || '',
        // File display / legacy fallback
        receipt_path: exp.receipt_path || null,
        receipt_data: exp.receipt_data || null,
        receipt_filename: exp.receipt_filename || null,
        _markDeleted: false,
        _file: null,
    };
}

function emptyRow() {
    return {
        id: undefined,
        receipt_type: '',
        amount: '',
        currency: 'SEK',
        description: '',
        payment_method: 'personal_card',
        project_id: '',
        activity_id: '',
        activity_name: '',
        billable: false,
        notes: '',
        receipt_path: null,
        receipt_data: null,
        receipt_filename: null,
        _markDeleted: false,
        _file: null,
    };
}

const TEXT_FIELDS = [
    'receipt_type', 'amount', 'currency', 'description',
    'payment_method', 'project_id', 'activity_id', 'billable', 'notes',
];

function hasChanged(current, original) {
    return TEXT_FIELDS.some((f) => String(current[f] ?? '') !== String(original[f] ?? ''));
}

/**
 * `original` is the row's initialSnapshot entry (undefined only if it's
 * somehow missing, treated as "changed" — the safe default).
 *
 * 23.R4: activity_id is included ONLY when it actually changed from the
 * snapshot. The backend re-validates the activity whenever this key is
 * present in the payload at all — so unconditionally sending
 * `row.activity_id || null` (the previous behaviour) re-triggered that
 * validation on every save of a row that HAS an activity, even when the
 * edit was a plain text-field or file-replacement change. If that
 * activity had since been deleted from the project, the unrelated edit
 * was rejected — exactly the case KD-083 / finding 5.175 (Principle 2,
 * "No Data Loss, Ever") was written to protect. Omitting the key when
 * unchanged keeps a legacy/edited row editable; when the user does
 * change (or clear) the activity, row.activity_id already differs from
 * original.activity_id and gets sent.
 */
function extractUpdatePayload(row, original) {
    const payload = {
        receipt_type: row.receipt_type,
        amount: parseFloat(row.amount) || 0,
        currency: row.currency,
        description: row.description || null,
        payment_method: row.payment_method,
        project_id: row.project_id || null,
        billable: row.billable,
        notes: row.notes || null,
    };
    const activityChanged = !original || String(row.activity_id ?? '') !== String(original.activity_id ?? '');
    if (activityChanged) {
        payload.activity_id = row.activity_id || null;
    }
    return payload;
}

function extractCreatePayload(row) {
    return {
        receipt_type: row.receipt_type,
        amount: parseFloat(row.amount) || 0,
        currency: row.currency,
        description: row.description || null,
        payment_method: row.payment_method,
        project_id: row.project_id || null,
        activity_id: row.activity_id || null,
        billable: row.billable,
        notes: row.notes || null,
    };
}

/**
 * Derive a display filename from receipt_path or receipt_filename.
 * receipt_path is "YYYY/MM/slug/uuid.ext" — take the last segment.
 */
function displayFilename(row) {
    if (row.receipt_filename) return row.receipt_filename;
    if (row.receipt_path) {
        const parts = row.receipt_path.split('/');
        return parts[parts.length - 1] || row.receipt_path;
    }
    return null;
}

/**
 * Best-guess MIME type from a filename extension for legacy base64 records.
 */
function mimeFromFilename(filename) {
    if (!filename) return 'application/octet-stream';
    const ext = filename.split('.').pop().toLowerCase();
    const map = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        pdf: 'application/pdf',
        heic: 'image/heic',
        heif: 'image/heif',
    };
    return map[ext] || 'application/octet-stream';
}

/**
 * DayReceiptForm
 *
 * Multi-entry receipt form for a single date. Shows existing receipt expenses
 * for the day, allows inline text-field editing, soft-delete with Undo, and
 * adding new receipts with a file upload.
 *
 * New uploads use the Phase 2 disk flow:
 *   uploadReceiptFile(file, date) → { file_path } → createReceiptExpense({ ...fields, receipt_path })
 *
 * Existing records with only receipt_data (legacy base64) stay viewable via
 * a data URL constructed from receipt_data + best-guess MIME.
 *
 * One Save button batches all operations via Promise.allSettled.
 *
 * In bulk mode (`dates` provided), `existingReceipts` is always empty (parent
 * passes []), so there's naturally just one blank add-row; the attachment
 * requirement is dropped (no per-date file picker makes sense) and Save
 * creates the row for every date in `dates` via the shared POST
 * /expenses/bulk endpoint (receipt has no date-relative fields).
 *
 * Props:
 *   selectedDay       {string}   — "YYYY-MM-DD"; single-day mode
 *   dates             {string[]} — ISO dates; bulk mode
 *   existingReceipts  {Array}    — expense docs (expense_category="receipt") for selectedDay
 *   projects          {Array}    — active project list [{id, name}]
 *   onSaved           {function} — () => void called after a successful batch save
 *   onDirtyChange     {function} — (dirty: bool) => void
 */
const DayReceiptForm = forwardRef(function DayReceiptForm({
    selectedDay,
    dates,
    existingReceipts = [],
    projects = [],
    onSaved,
    onDirtyChange,
    // Lets the host dialog widen to show a row's document beside its form,
    // the way SupplierInvoicesPage does — see hasAnyDocument below.
    onDocumentPreviewChange,
    initialFile = null,
}, ref) {
    const { t } = useTranslation();
    const isBulkMode = Array.isArray(dates) && dates.length > 0;

    // Retry set for bulk mode — starts as the full selection, narrows to only
    // the failed dates after a partial failure so a retry doesn't resubmit
    // (duplicate) the dates that already succeeded. Reset whenever the
    // parent hands us a new selection.
    const [bulkDates, setBulkDates] = React.useState(dates || []);
    React.useEffect(() => {
        if (isBulkMode) setBulkDates(dates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dates]);

    // ── State ──────────────────────────────────────────────────────────────────
    const [rows, setRows] = React.useState(() => {
        const existing = existingReceipts.map(seedRowFromExpense);
        // When there are existing receipts, show only those + "Add another" button.
        // When the day is empty, show one blank add-row so user can just type + attach.
        return existing.length > 0 ? existing : [emptyRow()];
    });
    const [saving, setSaving] = React.useState(false);

    // Track in-progress blob URL fetches so we can show a spinner per row
    const [viewingRowId, setViewingRowId] = React.useState(null);

    // Track an in-progress attachment removal so we can disable/spinner that row
    const [removingRowId, setRemovingRowId] = React.useState(null);

    // Mobile toggle for each row's document preview column — keyed by row
    // index, mirrors SupplierInvoicesPage's single previewOpenMobile flag.
    const [previewOpenMobile, setPreviewOpenMobile] = React.useState({});

    // ── FX preview state — one entry per row index ────────────────────────────
    // Each entry: { status: 'idle'|'loading'|'success'|'error', amount_sek, rate, rate_date }
    const [fxPreviews, setFxPreviews] = React.useState({});

    // Snapshot of initial text-field state keyed by id for dirty detection
    const initialSnapshot = useRef(
        existingReceipts.reduce((acc, exp) => {
            acc[exp.id] = seedRowFromExpense(exp);
            return acc;
        }, {})
    );

    // ── Re-seed when selectedDay or existingReceipts changes ──────────────────
    React.useEffect(() => {
        const seeded = existingReceipts.map(seedRowFromExpense);
        setRows(seeded.length > 0 ? seeded : [emptyRow()]);
        initialSnapshot.current = existingReceipts.reduce((acc, exp) => {
            acc[exp.id] = seedRowFromExpense(exp);
            return acc;
        }, {});
        onDirtyChange?.(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDay, existingReceipts]);

    // ── Pre-load a file captured via the quick-capture shortcut ──────────────
    // When an initialFile is provided (a File object), populate the single blank
    // add-row's _file field so the user doesn't have to re-select the file.
    // This runs only when initialFile changes and only when the form has exactly
    // one blank new row (the default empty-day state). Compressed the same way
    // as a manually-picked file (handleFileChange) — this is the camera-capture
    // path, so it's the most likely source of large, uncompressed photos.
    React.useEffect(() => {
        if (!(initialFile instanceof File)) return;
        let cancelled = false;
        (async () => {
            const compressedFile = await compressReceiptImage(initialFile);
            if (cancelled) return;
            setRows(prev => {
                if (prev.length !== 1 || prev[0].id !== undefined) return prev;
                return [{ ...prev[0], _file: compressedFile }];
            });
        })();
        return () => { cancelled = true; };
    }, [initialFile]);

    // ── Dirty computation ─────────────────────────────────────────────────────
    const isDirty = React.useMemo(() => {
        for (const row of rows) {
            const isNew = !row.id;
            if (isNew && !row._markDeleted) {
                // New row is dirty if any meaningful field has a value
                if (row._file || row.amount || row.receipt_type) return true;
            } else if (!isNew) {
                if (row._markDeleted) return true;
                // Existing row dirty if text fields changed OR a replacement file was picked
                if (row._file) return true;
                const orig = initialSnapshot.current[row.id];
                if (orig && hasChanged(row, orig)) return true;
            }
        }
        return false;
    }, [rows]);

    React.useEffect(() => {
        onDirtyChange?.(isDirty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDirty]);

    // Whether ANY row currently has a document to preview — a replacement
    // file, a stored disk path, or a legacy base64 blob. The host dialog uses
    // this to widen only while there is something worth showing beside the
    // form (Phase 26, F3).
    const hasAnyDocument = React.useMemo(
        () => rows.some((r) => !!r._file || !!r.receipt_path || !!r.receipt_data),
        [rows]
    );
    React.useEffect(() => {
        onDocumentPreviewChange?.(hasAnyDocument);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAnyDocument]);

    // ── FX preview fetch (debounced, per-row) ─────────────────────────────────
    // Watches every row's amount, currency, and selectedDay. For rows where
    // currency !== SEK and amount is valid, fetches GET /fx/preview after 300ms.
    // Uses a stale-check guard instead of AbortController so multiple rows can
    // fire independently without cancelling each other.
    React.useEffect(() => {
        // Build an array of timers, one per row. Return a cleanup that clears all.
        const timers = rows.map((row, index) => {
            const amount = parseFloat(row.amount);
            const shouldFetch = row.currency && row.currency !== 'SEK' &&
                !row._markDeleted && amount > 0 && !isNaN(amount);

            if (!shouldFetch) {
                // Clear any stale preview for this row
                setFxPreviews(prev => {
                    if (!prev[index]) return prev;
                    const next = { ...prev };
                    delete next[index];
                    return next;
                });
                return null;
            }

            // Mark loading immediately so there is no stale success showing
            setFxPreviews(prev => ({ ...prev, [index]: { status: 'loading' } }));

            let cancelled = false;
            const timer = setTimeout(async () => {
                if (cancelled) return;
                try {
                    // Preview only — the actual per-date rate is looked up
                    // server-side when each bulk date's record is created, so
                    // using the first selected date here is a representative
                    // estimate, not the value that gets persisted.
                    const res = await getFxPreview({
                        from: row.currency,
                        date: isBulkMode ? dates[0] : selectedDay,
                        amount,
                    });
                    if (cancelled) return;
                    const { amount_sek, exchange_rate, rate_date } = res.data;
                    setFxPreviews(prev => ({
                        ...prev,
                        [index]: { status: 'success', amount_sek, exchange_rate, rate_date },
                    }));
                } catch {
                    if (cancelled) return;
                    setFxPreviews(prev => ({
                        ...prev,
                        [index]: { status: 'error' },
                    }));
                }
            }, 300);

            return () => {
                cancelled = true;
                clearTimeout(timer);
            };
        });

        return () => {
            timers.forEach(cleanup => cleanup && cleanup());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows.map(r => `${r.amount}|${r.currency}|${r._markDeleted}`).join(','), selectedDay]);

    // ── Row mutation helpers ───────────────────────────────────────────────────
    const updateRowField = (rowIndex, field, value) => {
        setRows(prev => prev.map((r, i) => i === rowIndex ? { ...r, [field]: value } : r));
    };

    const markRowDeleted = (rowIndex) => {
        setRows(prev => prev.map((r, i) => i === rowIndex ? { ...r, _markDeleted: true } : r));
    };

    const undoRowDelete = (rowIndex) => {
        setRows(prev => prev.map((r, i) => i === rowIndex ? { ...r, _markDeleted: false } : r));
    };

    const addRow = () => {
        setRows(prev => [...prev, emptyRow()]);
    };

    // Compresses image receipts (PDFs pass through unchanged) before they're
    // attached to the row, so large phone-camera photos don't hit the 2MB
    // guard below. The guard stays as a backstop for files compression can't
    // shrink enough (or any non-image file that skips compression).
    const handleFileChange = async (rowIndex, e) => {
        const file = e.target.files[0] || null;
        if (!file) return;

        const compressedFile = await compressReceiptImage(file);

        if (compressedFile.size > 2 * 1024 * 1024) {
            toast.error('File size must be less than 2MB');
            e.target.value = '';
            return;
        }
        setRows(prev => prev.map((r, i) => i === rowIndex ? { ...r, _file: compressedFile } : r));
    };

    // ── View existing file ─────────────────────────────────────────────────────
    // For new disk-stored receipts: fetch via authenticated axios → blob URL → window.open.
    // For legacy base64 records: construct a data URL and open directly.
    const handleViewFile = async (row) => {
        // Legacy base64 path
        if (!row.receipt_path && row.receipt_data) {
            const mime = mimeFromFilename(row.receipt_filename);
            // receipt_data may already be a full data URL (older records) or raw base64
            const src = row.receipt_data.startsWith('data:')
                ? row.receipt_data
                : `data:${mime};base64,${row.receipt_data}`;
            window.open(src, '_blank');
            return;
        }

        // Phase 2 disk path — fetch through the authenticated axios client
        if (row.receipt_path) {
            setViewingRowId(row.id);
            try {
                const { default: api } = await import('../lib/api');
                const response = await api.get(`/expenses/receipt/${row.receipt_path}`, {
                    responseType: 'blob',
                });
                const blobUrl = URL.createObjectURL(response.data);
                const win = window.open(blobUrl, '_blank');
                // Revoke the blob URL after the window has had a chance to load it
                if (win) {
                    setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
                } else {
                    // Popup blocked — trigger a download instead
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = displayFilename(row) || 'receipt';
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
                }
            } catch {
                toast.error('Could not open receipt file');
            } finally {
                setViewingRowId(null);
            }
        }
    };

    // ── Remove attachment (immediate, not batched into Save) ────────────────────
    // Clears the file from an existing receipt record while keeping the record
    // itself. Confirmed via a native dialog (mirrors day-time-entries-form's
    // window.confirm pattern). On success, clears the row's local attachment
    // state and the dirty snapshot so the change sticks without a Save.
    const handleRemoveAttachment = async (row) => {
        // Only persisted rows have a server-side attachment to remove.
        if (!row.id) return;
        // Don't start a removal while a save is in flight (or another removal) —
        // serialize per-row mutations so state can't be clobbered mid-operation.
        if (saving || removingRowId) return;
        const confirmed = window.confirm(t('expenses.receipt.remove_attachment_confirm'));
        if (!confirmed) return;

        setRemovingRowId(row.id);
        try {
            await removeReceiptAttachment(row.id);
            // Match by stable receipt id, NOT array index — rows can be reseeded
            // (e.g. a parent refresh) between render and this async callback, so an
            // index captured at click time could point at the wrong row.
            setRows(prev => prev.map(r => r.id === row.id
                ? { ...r, receipt_path: null, receipt_data: null, receipt_filename: null, _file: null }
                : r));
            // Keep the dirty snapshot in sync so a later re-render/dirty check
            // doesn't treat the (already-persisted) removal as a pending change.
            const snapshot = initialSnapshot.current[row.id];
            if (snapshot) {
                initialSnapshot.current[row.id] = {
                    ...snapshot,
                    receipt_path: null,
                    receipt_data: null,
                    receipt_filename: null,
                };
            }
            toast.success(t('expenses.receipt.remove_attachment_success'));
        } catch {
            toast.error(t('expenses.receipt.remove_attachment_error'));
        } finally {
            setRemovingRowId(null);
        }
    };

    // ── Validation helper ──────────────────────────────────────────────────────
    const validateBeforeSave = () => {
        // Project is mandatory on every receipt (backend requires it — a
        // missing project_id 422s) — applies in both single-day and bulk mode.
        for (const row of rows) {
            // A row being deleted is never validated — an existing legacy row
            // with no project_id must still be deletable.
            if (row._markDeleted) continue;
            const isNew = !row.id;
            const hasData = row.amount || row.receipt_type || row._file;
            if (isNew && !hasData) continue;
            // Only rows this save will actually send are validated: a new row
            // with data, an existing row with a replacement file, or one the
            // user changed. An untouched legacy row with no project_id isn't
            // re-sent, so requiring one would block the rest of the form.
            const original = initialSnapshot.current[row.id];
            const willBeSent = isNew || row._file || (original && hasChanged(row, original));
            if (willBeSent && !row.project_id) {
                toast.error(t('expenses.receipt.project_required'));
                return false;
            }
            // Phase 23 (PRD 12.2, 23.R3): mirrors backend/utils/activities.py's
            // resolve_activity, which on an UPDATE only re-validates
            // activity_id when the row is new, its project changed, or the
            // caller is actively changing activity_id — an edit that touches
            // neither (including a plain file replacement) stays permissive
            // server-side, so a legacy row with no activity remains editable
            // on unrelated fields (Principle 2 "No Data Loss, Ever"; this is
            // what keeps rows editable after an activity is deleted from a
            // project, finding 5.175, and it has tests). Gating on
            // willBeSent alone was stricter than the server and forced an
            // activity pick for e.g. an amount-only correction on a legacy
            // row.
            const projectChanged = !original || String(row.project_id ?? '') !== String(original.project_id ?? '');
            const activityChanged = !original || String(row.activity_id ?? '') !== String(original.activity_id ?? '');
            const activityMustBeValidated = isNew || projectChanged || activityChanged;
            if (activityMustBeValidated && row.project_id) {
                const selectedProject = projects?.find(p => p.id === row.project_id);
                const projectActivities = selectedProject?.activities || [];
                if (projectActivities.length > 0 && !row.activity_id) {
                    toast.error(t('error.activities.required'));
                    return false;
                }
            }
        }

        // Bulk mode never requires an attachment (KD-072) — a receipt applied
        // to N dates can't sensibly carry one file per date; the user attaches
        // later via the day-view edit, same as any receipt that had its
        // attachment removed (Task 5.52).
        if (isBulkMode) return true;
        for (const row of rows) {
            if (row.id || row._markDeleted) continue;
            // Row has data but no file attached
            const hasData = row.amount || row.receipt_type;
            if (hasData && !row._file) {
                toast.error('Attach a file for each new receipt before saving');
                return false;
            }
        }
        return true;
    };

    // ── Save handler ───────────────────────────────────────────────────────────
    const handleSave = async () => {
        // Don't save while an attachment removal is still in flight — serialize
        // per-row mutations so a save can't race the removal's state update.
        if (removingRowId) return;
        if (!validateBeforeSave()) return;

        setSaving(true);

        if (isBulkMode) {
            const row = rows[0];
            if (!row || (!row.amount && !row.receipt_type)) {
                setSaving(false);
                onDirtyChange?.(false);
                onSaved?.();
                return;
            }
            try {
                const res = await createBulkExpense(bulkDates, 'receipt', extractCreatePayload(row));
                const failed = res.data?.failed || [];
                if (failed.length > 0) {
                    // Narrow the retry set to only the dates that actually
                    // failed — the ones that succeeded must not be resubmitted.
                    setBulkDates(failed.map(f => f.date));
                    toast.error(t('expenses.receipt.save_error'));
                    throw new Error('Receipt bulk save failed');
                }
                hapticSave();
                setRows([emptyRow()]); // fully saved — reset so a re-save can't duplicate
                onDirtyChange?.(false);
                onSaved?.();
            } finally {
                setSaving(false);
            }
            return;
        }

        const ops = [];
        const opLabels = [];

        for (const row of rows) {
            const isNew = !row.id;
            const isDeleted = row._markDeleted;

            // New row dropped before save — skip
            if (isNew && isDeleted) continue;

            // Pending delete of existing row
            if (!isNew && isDeleted) {
                ops.push(deleteExpenseV2(row.id));
                opLabels.push('delete');
                continue;
            }

            if (isNew) {
                // Skip entirely empty new rows (no file, no amount, no receipt_type)
                if (!row._file && !row.amount && !row.receipt_type) continue;

                // Phase 2 chain: upload file first, then create with receipt_path
                ops.push(
                    uploadReceiptFile(row._file, selectedDay).then(uploadRes =>
                        createReceiptExpense({
                            ...extractCreatePayload(row),
                            receipt_path: uploadRes.data.file_path,
                            date: selectedDay,
                        })
                    )
                );
                opLabels.push('create');
                continue;
            }

            // Existing row — two possible changes:
            //   1. Text fields changed: PUT with text updates.
            //   2. A replacement file was picked (row._file): upload to disk first,
            //      then PUT with receipt_path (+ clear any legacy receipt_data/filename).
            //   If both, we merge into a single PUT so text + file change are atomic.
            const original = initialSnapshot.current[row.id];
            const textChanged = original && hasChanged(row, original);
            if (row._file) {
                ops.push(
                    uploadReceiptFile(row._file, selectedDay).then(uploadRes =>
                        updateReceiptExpense(row.id, {
                            ...extractUpdatePayload(row, original),
                            receipt_path: uploadRes.data.file_path,
                            // Null out legacy base64 so it doesn't shadow the new disk file.
                            receipt_data: null,
                            receipt_filename: row._file.name,
                        })
                    )
                );
                opLabels.push('update');
            } else if (textChanged) {
                ops.push(updateReceiptExpense(row.id, extractUpdatePayload(row, original)));
                opLabels.push('update');
            }
        }

        if (ops.length === 0) {
            onDirtyChange?.(false);
            onSaved?.();
            setSaving(false);
            return;
        }

        const results = await Promise.allSettled(ops);
        const failures = results.filter(r => r.status === 'rejected');

        if (failures.length === 0) {
            hapticSave();
            onDirtyChange?.(false);
            onSaved?.();
        } else {
            // Check if any failure is a Riksbanken FX unavailability (503)
            const hasFxOutage = failures.some(r => {
                const err = r.reason;
                return (
                    err?.response?.status === 503 &&
                    err?.response?.data?.detail?.error_code === 'error.fx.unavailable'
                );
            });

            if (hasFxOutage) {
                toast.error(t('expenses.receipt.fx_failure_short'), {
                    description: t('expenses.receipt.fx_failure_hint'),
                    action: {
                        label: t('expenses.receipt.fx_lookup_link'),
                        onClick: () => window.open(
                            'https://www.riksbank.se/en-gb/statistics/interest-rates-and-exchange-rates/search-interest-and-exchange-rates/',
                            '_blank'
                        ),
                    },
                });
            } else {
                toast.error(
                    `${failures.length} receipt ${failures.length === 1 ? 'entry' : 'entries'} failed to save. Please review and try again.`
                );
            }
            setSaving(false);
            throw new Error('Receipt save failed');
        }

        setSaving(false);
    };

    // Expose save function to parent via ref (used by Save Day orchestrator)
    useImperativeHandle(ref, () => ({ save: handleSave }));

    // ── Render ─────────────────────────────────────────────────────────────────
    const visibleRows = rows.map((row, index) => ({ row, index }));

    return (
        <div className="space-y-4 py-2">
            {visibleRows.map(({ row, index }) => {
                const isExisting = !!row.id;
                const isDeleted = row._markDeleted;
                const isViewing = viewingRowId === row.id;
                const isRemoving = removingRowId === row.id;
                const filename = displayFilename(row);
                const hasViewableFile = !!(row.receipt_path || row.receipt_data);
                // One preview per row: a replacement file always wins over
                // the stored one, same rule SupplierInvoicesPage's dialogs use.
                const rowPreviewFile = row._file || null;
                const rowPreviewPath = !rowPreviewFile && row.receipt_path ? row.receipt_path : null;
                const rowLegacyOnly = !rowPreviewFile && !rowPreviewPath && !!row.receipt_data;
                const rowHasDoc = !!rowPreviewFile || !!rowPreviewPath || rowLegacyOnly;
                const rowPreviewOpen = !!previewOpenMobile[index];

                return (
                    <div
                        key={row.id ?? `new-${index}`}
                        className={`border border-border p-4 space-y-4 transition-opacity ${isDeleted ? 'opacity-40' : ''}`}
                    >
                        {/* Row header */}
                        <div className="flex justify-between items-center">
                            <span className={`text-xs uppercase tracking-widest text-muted-foreground ${isDeleted ? 'line-through' : ''}`}>
                                {isExisting ? `Receipt ${index + 1}` : 'New receipt'}
                            </span>
                            <div className="flex items-center gap-1">
                                {isExisting && !isDeleted && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => markRowDeleted(index)}
                                        className="min-h-[44px] min-w-[44px] p-0 text-destructive"
                                        disabled={saving}
                                        aria-label="Delete receipt"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                )}
                                {isExisting && isDeleted && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => undoRowDelete(index)}
                                        className="min-h-[44px] min-w-[44px] p-0"
                                        disabled={saving}
                                        aria-label="Undo delete"
                                    >
                                        <Undo2 className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Widens to a form column + document column, side by
                            side, only while this row actually has a document —
                            same rule as SupplierInvoicesPage's dialogs. */}
                        <div className={cn(rowHasDoc && 'lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start')}>

                        {/* Field grid — disabled when deleted or saving */}
                        <fieldset disabled={isDeleted || saving} className="space-y-4 border-0 p-0 m-0">
                            {/* Type */}
                            <div>
                                <Label className="label-uppercase mb-2 block">Type</Label>
                                <select
                                    value={row.receipt_type}
                                    onChange={(e) => updateRowField(index, 'receipt_type', e.target.value)}
                                    className="w-full h-10 px-3 border border-input bg-background text-sm"
                                >
                                    <option value="">-- Select type --</option>
                                    {RECEIPT_TYPES.map(r => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Amount + Currency */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="label-uppercase mb-2 block">Amount</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={row.amount}
                                        onChange={(e) => updateRowField(index, 'amount', e.target.value)}
                                        placeholder="0.00"
                                    />
                                </div>
                                <div>
                                    <Label className="label-uppercase mb-2 block">Currency</Label>
                                    <select
                                        value={row.currency}
                                        onChange={(e) => updateRowField(index, 'currency', e.target.value)}
                                        className="w-full h-10 px-3 border border-input bg-background text-sm"
                                    >
                                        {CURRENCIES.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* SEK conversion preview (foreign currency only) */}
                            {(() => {
                                const fx = fxPreviews[index];
                                if (!fx) return null;
                                if (fx.status === 'loading') {
                                    return (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {t('expenses.receipt.fx_loading')}
                                        </p>
                                    );
                                }
                                if (fx.status === 'success') {
                                    return (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {t('expenses.receipt.fx_preview', {
                                                amount: formatSEK(fx.amount_sek),
                                                rate: fx.exchange_rate,
                                                rateDate: fx.rate_date,
                                            })}
                                        </p>
                                    );
                                }
                                if (fx.status === 'error') {
                                    return (
                                        <div className="mt-1">
                                            <p className="text-xs text-amber-600">
                                                {t('expenses.receipt.fx_failure_short')}{' '}
                                                <a
                                                    href="https://www.riksbank.se/en-gb/statistics/interest-rates-and-exchange-rates/search-interest-and-exchange-rates/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="underline"
                                                >
                                                    {t('expenses.receipt.fx_lookup_link')}
                                                </a>
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {t('expenses.receipt.fx_failure_hint')}
                                            </p>
                                        </div>
                                    );
                                }
                                return null;
                            })()}

                            {/* Description */}
                            <div>
                                <Label className="label-uppercase mb-2 block">Description (optional)</Label>
                                <Input
                                    value={row.description}
                                    onChange={(e) => updateRowField(index, 'description', e.target.value)}
                                    placeholder="What was this for?"
                                />
                            </div>

                            {/* Payment method */}
                            <div>
                                <Label className="label-uppercase mb-2 block">Payment method</Label>
                                <select
                                    value={row.payment_method}
                                    onChange={(e) => updateRowField(index, 'payment_method', e.target.value)}
                                    className="w-full h-10 px-3 border border-input bg-background text-sm"
                                >
                                    {PAYMENT_METHODS.map(m => (
                                        <option key={m.id} value={m.id}>{m.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Project (required — backend rejects a missing project_id) */}
                            <div>
                                <Label className="label-uppercase mb-2 block">
                                    {t('expenses.receipt.project_label')} <span className="text-destructive">*</span>
                                </Label>
                                {projects.length > 0 ? (
                                    <select
                                        value={row.project_id}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            // Phase 23 (PRD 12.2): changing the project re-evaluates
                                            // whether an activity applies at all — clear a
                                            // now-invalid selection rather than silently carry an
                                            // activity id that belongs to the old project.
                                            setRows(prev => prev.map((r, i) => i === index
                                                ? { ...r, project_id: val, activity_id: '', activity_name: '' }
                                                : r));
                                        }}
                                        className="w-full h-10 px-3 border border-input bg-background text-sm"
                                        data-testid={`project-select-${index}`}
                                    >
                                        <option value="">{t('expenses.receipt.project_placeholder')}</option>
                                        {projects.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <p className="text-xs text-destructive">{t('expenses.receipt.no_projects_available')}</p>
                                )}
                            </div>

                            {row.project_id && (() => {
                                const selectedProject = projects?.find(p => p.id === row.project_id);
                                const projectActivities = selectedProject?.activities || [];
                                if (projectActivities.length === 0) return null;
                                // Phase 23 (PRD 12.2): shown ONLY when the project defines
                                // activities — a project with none renders no field at all
                                // (matches day-time-entries-form.jsx's pattern exactly).
                                return (
                                    <div>
                                        <Label className="label-uppercase mb-2 block">
                                            {t('timesheet.bulk_dialog.activity_label')} <span className="text-destructive">*</span>
                                        </Label>
                                        <select
                                            value={row.activity_id || ''}
                                            onChange={(e) => {
                                                const actId = e.target.value;
                                                const act = projectActivities.find(a => a.id === actId);
                                                setRows(prev => prev.map((r, i) => i === index
                                                    ? { ...r, activity_id: actId, activity_name: act ? act.name : '' }
                                                    : r));
                                            }}
                                            className="w-full h-10 px-3 border border-input bg-background text-sm"
                                            data-testid={`activity-select-${index}`}
                                            aria-required="true"
                                        >
                                            <option value="">{t('timesheet.bulk_dialog.activity_placeholder')}</option>
                                            {projectActivities.map(act => (
                                                <option key={act.id} value={act.id}>{act.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })()}

                            {/* Billable */}
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id={`billable-${index}`}
                                    checked={row.billable}
                                    onCheckedChange={(checked) => updateRowField(index, 'billable', !!checked)}
                                />
                                <Label htmlFor={`billable-${index}`} className="text-sm cursor-pointer">
                                    Billable to client
                                </Label>
                            </div>

                            {/* Notes */}
                            <div>
                                <Label className="label-uppercase mb-2 block">Notes (optional)</Label>
                                <Input
                                    value={row.notes}
                                    onChange={(e) => updateRowField(index, 'notes', e.target.value)}
                                    placeholder="Additional notes..."
                                />
                            </div>

                            {/* File section — skipped entirely in bulk mode (KD-072): a
                                receipt applied to N dates can't carry one file per date;
                                attach later via the day-view edit. Compact controls only —
                                the document itself renders in the preview column beside
                                this form (or below it on mobile, via the toggle there). */}
                            {isBulkMode ? (
                                <p className="text-xs text-muted-foreground">{t('expenses.receipt.bulk_no_attachment_hint')}</p>
                            ) : isExisting ? (
                                /* Existing row — filename + View + Replace */
                                <div className="space-y-2">
                                    <Label className="label-uppercase mb-2 block">Attached file</Label>

                                    {hasViewableFile && (
                                        <div className="flex items-center gap-3">
                                            {filename && (
                                                <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                                                    {filename}
                                                </span>
                                            )}
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleViewFile(row)}
                                                disabled={isViewing || isRemoving || saving}
                                                aria-label="View receipt file"
                                            >
                                                {isViewing ? (
                                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                ) : (
                                                    <ExternalLink className="w-4 h-4 mr-1" />
                                                )}
                                                {isViewing ? 'Opening...' : 'View'}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveAttachment(row)}
                                                disabled={isViewing || isRemoving || saving}
                                                className="text-destructive hover:text-destructive"
                                                aria-label={t('expenses.receipt.remove_attachment_label')}
                                            >
                                                {isRemoving ? (
                                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                ) : (
                                                    <FileX className="w-4 h-4 mr-1" />
                                                )}
                                                {t('expenses.receipt.remove_attachment_label')}
                                            </Button>
                                        </div>
                                    )}

                                    {/* Replace file (or upload one if the existing record has none) */}
                                    <div className="pt-1">
                                        <Label className="text-xs text-muted-foreground mb-1 block">
                                            {hasViewableFile ? 'Replace file (optional)' : 'Upload file'}
                                        </Label>
                                        <input
                                            type="file"
                                            accept={FILE_ACCEPT}
                                            capture="environment"
                                            onChange={(e) => handleFileChange(index, e)}
                                            className="w-full text-sm file:mr-4 file:py-1.5 file:px-3 file:border file:border-border file:text-xs file:font-medium file:bg-muted file:text-foreground hover:file:bg-muted/80"
                                            disabled={saving}
                                        />
                                        {row._file && (
                                            <div className="flex items-center gap-2 mt-1">
                                                <p className="text-xs text-muted-foreground">
                                                    Will replace with: <span className="font-medium">{row._file.name}</span>
                                                </p>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => updateRowField(index, '_file', null)}
                                                    className="min-h-[44px] px-2 text-xs text-destructive hover:text-destructive"
                                                    disabled={saving}
                                                >
                                                    Cancel replace
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                /* New row — file picker (required) */
                                <div>
                                    <Label className="label-uppercase mb-2 block">Receipt file <span className="text-destructive">*</span></Label>
                                    <input
                                        type="file"
                                        accept={FILE_ACCEPT}
                                        capture="environment"
                                        onChange={(e) => handleFileChange(index, e)}
                                        className="w-full text-sm file:mr-4 file:py-1.5 file:px-3 file:border file:border-border file:text-xs file:font-medium file:bg-muted file:text-foreground hover:file:bg-muted/80"
                                        disabled={saving}
                                    />
                                    {row._file && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Selected: {row._file.name}
                                        </p>
                                    )}
                                </div>
                            )}
                        </fieldset>

                        {/* Document preview column — one per row, beside the
                            form on lg+, toggled below it on mobile. Rendered
                            only when this row actually has something to show;
                            unmounting SupplierInvoicePreview on delete/replace
                            revokes its object URL. */}
                        {rowHasDoc && (
                            <div className="min-w-0 mt-4 lg:mt-0 flex flex-col">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="lg:hidden mb-2 uppercase tracking-wider"
                                    onClick={() => setPreviewOpenMobile((prev) => ({ ...prev, [index]: !prev[index] }))}
                                    aria-expanded={rowPreviewOpen}
                                    aria-controls={`sr-preview-${index}`}
                                >
                                    <FileText className="w-4 h-4 mr-2" />
                                    {rowPreviewOpen ? t('supplier_invoices.preview_hide') : t('supplier_invoices.preview_show')}
                                </Button>
                                {rowPreviewFile || rowPreviewPath ? (
                                    <SupplierInvoicePreview
                                        id={`sr-preview-${index}`}
                                        file={rowPreviewFile}
                                        filePath={rowPreviewFile ? null : rowPreviewPath}
                                        fileName={rowPreviewFile ? rowPreviewFile.name : filename}
                                        fetchBlob={getReceiptFileBlob}
                                        className={cn('h-[50vh] lg:h-[65vh]', rowPreviewOpen ? 'flex' : 'hidden lg:flex')}
                                    />
                                ) : (
                                    <div
                                        id={`sr-preview-${index}`}
                                        className={cn(rowPreviewOpen ? 'block' : 'hidden lg:block')}
                                    >
                                        <LegacyDataUrlThumbnail
                                            receiptData={row.receipt_data}
                                            receiptFilename={row.receipt_filename}
                                            onClick={() => handleViewFile(row)}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        </div>

                        {isDeleted && (
                            <p className="text-xs text-muted-foreground">
                                Will be deleted on Save.{' '}
                                <button
                                    type="button"
                                    onClick={() => undoRowDelete(index)}
                                    className="underline hover:no-underline inline-flex items-center min-h-[44px] px-1"
                                    disabled={saving}
                                >
                                    Undo
                                </button>
                            </p>
                        )}
                    </div>
                );
            })}

            {/* Add another row — hidden in bulk mode: a bulk save only ever
                submits rows[0] (the one entry applied to every selected
                date), so a second row would silently be discarded. */}
            {!isBulkMode && (
                <Button
                    variant="outline"
                    className="w-full uppercase tracking-wider text-xs"
                    onClick={addRow}
                    disabled={saving}
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add another receipt
                </Button>
            )}

        </div>
    );
});

export default DayReceiptForm;
